import React, { useCallback, useMemo, useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import RediscoveryFilterRow from 'talent_matching/talent_rediscovery/components/filters/rediscovery_filter_row';
import RediscoveryTableWrapper from 'talent_matching/talent_rediscovery/components/table/rediscovery_table_wrapper';
import {
  TalentSearchQueryParams,
  type SavedSearch,
} from 'talent_search/shared/types';
import type { RediscoveryPageDefaults } from 'talent_matching/talent_rediscovery/types';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import type { AdvancedFilterType } from 'talent_matching/types';
import {
  toSavedSearchQueryString,
  extractFormFieldsFromSavedSearch,
} from 'talent_search/lib/query_params';
import { useRediscoveryColumns } from 'talent_matching/talent_rediscovery/components/table/rediscovery_columns';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import {
  ACTIONS_COLUMN_FIELD,
  useColumnVisibility,
  NON_TOGGLABLE_COLUMNS,
} from 'talent_matching/hooks/use_column_visibility';
import {
  GridColumnVisibilityModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import SavedFilters from 'talent_matching/components/filters/saved_filters';
import { useSavedFilters } from 'talent_matching/hooks/use_saved_filters';
import RediscoveryHeaderRow from 'talent_matching/talent_rediscovery/components/table/rediscovery_header_row';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { setDefaultSavedSearch } from 'talent_matching/api';

const REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL: GridColumnVisibilityModel = {
  fullName: true,
  latestApplicationHistory: true,
  summary: true,
  tags: false,
  [ACTIONS_COLUMN_FIELD]: true,
};

type RediscoveryPageContentProps = {
  clearedValues: RediscoveryPageDefaults;
};

const RediscoveryPageContent = ({
  clearedValues,
}: RediscoveryPageContentProps) => {
  const {
    savedSearchOptions,
    setSavedSearchOptions,
    savedFilterSet,
    setSavedFilterSet,
    suggestedFilterSet,
    greenhouseFilterPresets,
  } = useRediscoveryTabContext();
  const {
    hiringPlanId,
    paths: { defaultSavedSearchPath },
  } = useUserContext();

  const {
    getValues,
    setValue,
    control,
    reset,
    formState: { dirtyFields },
  } = useFormContext();
  const userDateFormat = useWatch({ control, name: 'user_date_format' }) ?? '';

  const [filteredTotalRows, setFilteredTotalRows] = useState<number>(0);
  const [isLoadingCandidates, setIsLoadingCandidates] = useState<boolean>(true);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [clearSelectionsKey, setClearSelectionsKey] = useState(0);

  const [normalizedColumnVisibilityModel, setColumnVisibilityModel] =
    useColumnVisibility({
      storageKey: 'talent-rediscovery-column-visibility',
      defaults: REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL,
    });

  const parseSavedSearch = useCallback(
    (response: unknown) =>
      extractFormFieldsFromSavedSearch(
        response as SavedSearch,
        userDateFormat
      ) as Record<string, unknown>,
    [userDateFormat]
  );

  const buildQueryString = useCallback(() => {
    const formValues = getValues();
    formValues.advancedFilters = (formValues.advancedFilters || []).filter(
      (filter: AdvancedFilterType) => new AdvancedFilter(filter).applied()
    );
    return toSavedSearchQueryString(formValues as TalentSearchQueryParams);
  }, [getValues]);

  // "Use suggested filter" — applies the smart Greenhouse-suggested filter to
  // the form, updates the chip, and persists the underlying preset key so the
  // same suggestion auto-applies on the next visit.
  const handleReset = useCallback(() => {
    if (!suggestedFilterSet) {
      return;
    }

    setSavedFilterSet({
      label: suggestedFilterSet.label,
      value: suggestedFilterSet.value,
    });
    reset(clearedValues);
    Object.keys(suggestedFilterSet.formValues).forEach((key) => {
      setValue(
        key,
        suggestedFilterSet.formValues[
          key as keyof typeof suggestedFilterSet.formValues
        ],
        { shouldDirty: true }
      );
    });
    setDefaultSavedSearch(defaultSavedSearchPath, {
      hiringPlanId,
      featureType: 'talent_matching_rediscovery',
      greenhouseFilterKey: suggestedFilterSet.value,
    });
  }, [
    suggestedFilterSet,
    setSavedFilterSet,
    reset,
    setValue,
    clearedValues,
    defaultSavedSearchPath,
    hiringPlanId,
  ]);

  const { savedFiltersProps, handleClearAll } = useSavedFilters({
    featureType: 'talent_matching_rediscovery',
    formDefaults: clearedValues,
    savedFilterSet,
    setSavedFilterSet,
    savedSearchOptions,
    setSavedSearchOptions,
    parseSavedSearch,
    buildQueryString,
    greenhouseFilterPresets,
    rememberLastUsed: true,
  });

  const hasAnyFilters = Object.keys(dirtyFields).length > 0;

  const columns = useRediscoveryColumns({ userDateFormat });
  const columnSelectorColumns = useMemo(() => {
    return columns
      .filter((c) => !NON_TOGGLABLE_COLUMNS.includes(c.field))
      .map((c) => ({
        field: c.field,
        label: c.headerName || c.field,
      }));
  }, [columns]);

  const onRowSelected = useCallback((selectedRows: GridRowSelectionModel) => {
    setSelectedIds(selectedRows.map((id) => Number(id)));
  }, []);

  const onClearSelections = useCallback(() => {
    setSelectedIds([]);
    setClearSelectionsKey((k) => k + 1);
  }, []);

  return (
    <Stack spacing={0}>
      <RediscoveryHeaderRow
        totalCount={filteredTotalRows}
        totalSelected={selectedIds.length}
        noneSelected={selectedIds.length === 0}
        applicationIds={selectedIds}
        onClear={onClearSelections}
        columnSelectorColumns={columnSelectorColumns}
        columnVisibilityModel={normalizedColumnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        defaultColumnVisibilityModel={
          REDISCOVERY_DEFAULT_COLUMN_VISIBILITY_MODEL
        }
        isLoading={isLoadingCandidates}
      />
      <RediscoveryFilterRow
        onClearAll={handleClearAll}
        onReset={handleReset}
        formDefaults={clearedValues}
      >
        <SavedFilters {...savedFiltersProps} />
      </RediscoveryFilterRow>

      <RediscoveryTableWrapper
        onTotalRowsChange={setFilteredTotalRows}
        onLoadingChange={setIsLoadingCandidates}
        columnVisibilityModel={normalizedColumnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        hasAnyFilters={hasAnyFilters}
        onClearAll={handleClearAll}
        onRowSelected={onRowSelected}
        onClearSelections={onClearSelections}
        clearSelectionsKey={clearSelectionsKey}
      />
    </Stack>
  );
};

export default RediscoveryPageContent;
